(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.HPQuizLogic = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var PRODUCTS = {
    pure: { id: 'pure', name: 'Hidra Pure', capacityMl: 600 },
    plus: { id: 'plus', name: 'Hidra Plus', capacityMl: 2000 }
  };

  var PRODUCT_ALIASES = {
    pure: 'pure',
    hidrapure: 'pure',
    plus: 'plus',
    hidraplus: 'plus'
  };

  var CONTAINER_CAPACITY_ML = {
    ate600: 600,
    ate1l: 1000,
    ate2l: 2000,
    galao: 5000
  };

  function normalizeKey(value) {
    if (typeof value !== 'string') return '';
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  function normalizeProduct(value) {
    return PRODUCT_ALIASES[normalizeKey(value)] || null;
  }

  function parsePositiveQuantity(value) {
    if (typeof value === 'number') {
      return Number.isSafeInteger(value) && value > 0 ? value : null;
    }
    if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return null;
    var parsed = Number(value.trim());
    return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function parseOrderQuantity(value) {
    if (typeof value === 'number') return Number.isSafeInteger(value) && value >= 0 ? value : null;
    if (typeof value !== 'string' || !/^\d+$/.test(value.trim())) return null;
    var parsed = Number(value.trim());
    return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
  }

  function isValidEmail(value) {
    if (typeof value !== 'string') return false;
    var email = value.trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  function getParam(source, key) {
    if (source && typeof source.get === 'function') return source.get(key);
    if (typeof source === 'string') return new URLSearchParams(source).get(key);
    if (source && Object.prototype.hasOwnProperty.call(source, key)) return source[key];
    return null;
  }

  function hasParam(source, key) {
    if (source && typeof source.has === 'function') return source.has(key);
    if (typeof source === 'string') return new URLSearchParams(source).has(key);
    return !!source && Object.prototype.hasOwnProperty.call(source, key);
  }

  function resolveContext(source) {
    var rawProduct = getParam(source, 'prod');
    var rawQuantity = getParam(source, 'q');
    var product = normalizeProduct(rawProduct);
    var quantity = parsePositiveQuantity(rawQuantity);
    var hasPureQuantity = hasParam(source, 'pure_q');
    var hasPlusQuantity = hasParam(source, 'plus_q');
    var pureQuantity = hasPureQuantity ? parseOrderQuantity(getParam(source, 'pure_q')) : null;
    var plusQuantity = hasPlusQuantity ? parseOrderQuantity(getParam(source, 'plus_q')) : null;
    var isMixedOrder = pureQuantity > 0 && plusQuantity > 0;
    var errors = [];

    if (!rawProduct) errors.push(isMixedOrder ? 'mixed_product_not_selected' : 'missing_product');
    else if (!product) errors.push('unknown_product');

    if (hasPureQuantity && pureQuantity === null) errors.push('invalid_pure_quantity');
    if (hasPlusQuantity && plusQuantity === null) errors.push('invalid_plus_quantity');

    var contextualQuantity = product === 'pure' ? pureQuantity : plusQuantity;
    if (product && contextualQuantity === 0) {
      errors.push('product_not_in_order');
    } else if (product && contextualQuantity !== null) {
      if (hasParam(source, 'q') && quantity === null) errors.push('invalid_quantity');
      if (quantity !== null && quantity !== contextualQuantity) errors.push('conflicting_quantity');
      quantity = contextualQuantity;
    } else if (quantity === null) {
      errors.push(hasParam(source, 'q') ? 'invalid_quantity' : 'missing_quantity');
    }

    var email = String(getParam(source, 'email') || '').trim();
    var orderId = String(getParam(source, 'p') || '').trim();
    var hasValidEmail = isValidEmail(email);
    var canBootstrap = errors.length === 1 && errors[0] === 'missing_quantity' &&
      !!product && !!orderId && hasValidEmail;
    return {
      ok: errors.length === 0,
      canBootstrap: canBootstrap,
      errors: errors,
      customerId: String(getParam(source, 'c') || '').trim(),
      orderId: orderId,
      email: email,
      hasValidEmail: hasValidEmail,
      product: product,
      quantity: quantity,
      isMixedOrder: isMixedOrder,
      orderQuantities: { pure: pureQuantity, plus: plusQuantity }
    };
  }

  function activeUnitsPerContainer(quantity, distribution) {
    var units = parsePositiveQuantity(quantity);
    if (units === null) return null;
    return distribution === 'juntas' ? units : 1;
  }

  function calculateDose(input) {
    var product = PRODUCTS[input.product];
    var targetMl = CONTAINER_CAPACITY_ML[input.container];
    var activeUnits = activeUnitsPerContainer(input.quantity, input.distribution);
    if (!product || !targetMl || activeUnits === null) return null;
    var coverageMl = product.capacityMl * activeUnits;
    return {
      activeUnits: activeUnits,
      coverageMl: coverageMl,
      targetMl: targetMl,
      isAdequate: coverageMl >= targetMl
    };
  }

  function calculateDuration(input) {
    var quantity = parsePositiveQuantity(input.quantity);
    var dailyLiters = Number(input.dailyLiters);
    var people = parsePositiveQuantity(input.people) || 1;
    if (quantity === null || !Number.isFinite(dailyLiters) || dailyLiters <= 0) return null;

    var consumptionPerUnit = input.distribution === 'juntas' ? dailyLiters * people : dailyLiters;
    var cycles = input.distribution === 'estoque' ? quantity : 1;
    var daysByVolume = Math.round(input.capacityLiters / consumptionPerUnit);
    var daysPerCycle = Math.min(daysByVolume, input.maxDays);

    return {
      consumptionPerUnit: consumptionPerUnit,
      cycles: cycles,
      daysByVolume: daysByVolume,
      daysPerCycle: daysPerCycle,
      totalDays: daysPerCycle * cycles,
      limit: daysByVolume < input.maxDays ? 'volume' : 'tempo'
    };
  }

  function buildClientEventPayload(email, properties) {
    if (!isValidEmail(email) || !properties || typeof properties !== 'object') return null;
    return {
      data: {
        type: 'event',
        attributes: {
          properties: properties,
          metric: { data: { type: 'metric', attributes: { name: 'Quiz Respondido' } } },
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email.trim(), properties: properties }
            }
          }
        }
      }
    };
  }

  function sendClientEvent(input) {
    var payload = buildClientEventPayload(input.email, input.properties);
    if (!payload) return Promise.reject(new Error('invalid_event_payload'));
    if (typeof input.fetchImpl !== 'function') return Promise.reject(new Error('fetch_unavailable'));

    return input.fetchImpl('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(input.companyId), {
      method: 'POST',
      headers: {
        'content-type': 'application/vnd.api+json',
        revision: input.revision
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('klaviyo ' + response.status);
      return response;
    });
  }

  return {
    PRODUCTS: PRODUCTS,
    CONTAINER_CAPACITY_ML: CONTAINER_CAPACITY_ML,
    normalizeProduct: normalizeProduct,
    parsePositiveQuantity: parsePositiveQuantity,
    parseOrderQuantity: parseOrderQuantity,
    isValidEmail: isValidEmail,
    resolveContext: resolveContext,
    activeUnitsPerContainer: activeUnitsPerContainer,
    calculateDose: calculateDose,
    calculateDuration: calculateDuration,
    buildClientEventPayload: buildClientEventPayload,
    sendClientEvent: sendClientEvent
  };
});
